<script setup>
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import { computed, inject, nextTick, onMounted, reactive, ref, watch } from "vue";
import JSONtoHTML from "@/components/forms/SharedComponents/WYSIWYG/JSONtoHTML.vue";
import {
  CLOUDINARY_DEFAULT_IMAGE_TRANSFORMATION,
  CLOUDINARY_VIDEO_EXTENSION_TYPES,
  CONTACT_HOURS,
  US_COLLEGE_STUDENT_ID,
  US_GRADUATE_STUDENT_ID,
} from "@/constants.js";
import { useGetEntitySupplementById } from "@/providers/hostInstitutions/useGetEntitySupplementById";
import { useGetHostInstitutionById } from "@/providers/hostInstitutions/useGetHostInstitutionById";
import { useGetHostInstitutionLanguageById } from "@/providers/hostInstitutions/useGetHostInstitutionLanguageById";
import { imageTransformation } from "@/util/cloudinary";
import StickyFooter from "./StickyFooter.vue";

const { setCredits } = inject("courses");
const { goToNextStep, goToPreviousStep } = inject("steps");
const { program, selectedSession } = inject("program");

const learnerTypesForInfoParagraph = [US_COLLEGE_STUDENT_ID, US_GRADUATE_STUDENT_ID];

const sessionHostingInstitution = computed(() => selectedSession.value?.session_hosting_institutions?.[0]?.entity_id);

const { data: hostInstitution } = useGetHostInstitutionById(sessionHostingInstitution);
const { data: hostInstitutionLanguages } = useGetHostInstitutionLanguageById(sessionHostingInstitution);
const { data: entitySupplement } = useGetEntitySupplementById(sessionHostingInstitution);

const form = reactive({
  credits: undefined,
});

const rules = {
  credits: { required },
};
const v$ = useVuelidate(rules, form);

const hasCredits = computed(() => {
  const minCredits = selectedSession.value?.academic_min_credits ?? 0;
  return minCredits > 0;
});

const minCredits = computed(() => selectedSession.value?.academic_min_credits ?? 0);
const maxCredits = computed(() => selectedSession.value?.academic_max_credits ?? minCredits.value);

const showMultipleCredits = computed(() => Boolean(minCredits.value !== maxCredits.value));

const isExpanded = ref(false);
const isToggleVisible = ref(false);
const paragraphRef = ref(null);

const isClamped = computed(() => !isExpanded.value);

const toggleExpansion = () => {
  isExpanded.value = !isExpanded.value;
};

const academicDescription = computed(() => {
  return selectedSession.value?.academic_description;
});

const hasAcademicDescription = computed(() => {
  const selectedAcademicDescription = selectedSession.value?.academic_description?.content;
  const selectedContent = Object.values(selectedAcademicDescription);
  return Boolean(selectedAcademicDescription) && Boolean(selectedContent[0]?.content);
});

const titleCopy = computed(() => {
  return showMultipleCredits.value
    ? `Up to ${maxCredits.value} credits are included in this program.`
    : `${minCredits.value} U.S. Semester Credits`;
});

const descriptionCopy = "No selection is required for this step.";

const setCredit = () => {
  form.credits = {
    label: maxCredits.value,
    value: maxCredits.value,
  };
  setCredits(maxCredits.value);
};

const isAcademicDescriptionClamped = (element) => {
  const descElement = element.querySelector("#json-to-html");
  if (descElement) {
    return descElement.scrollHeight > descElement.clientHeight;
  }
  return false;
};

const checkClamping = () => {
  nextTick(() => {
    const paragraph = paragraphRef.value?.$el ?? paragraphRef.value;
    if (paragraph) {
      isToggleVisible.value = isAcademicDescriptionClamped(paragraph);
    }
  });
};

watch(
  hasAcademicDescription,
  (newValue) => {
    if (newValue) {
      checkClamping();
      window.addEventListener("resize", checkClamping);
    }
  },
  { immediate: true },
);

const infoParagraphs = computed(() => {
  return [
    {
      title: "pre-requisites",
      description: selectedSession.value?.prerequisites,
      visible: !!selectedSession.value?.prerequisites,
    },
    {
      title: "Co-requisites",
      description: selectedSession.value?.corequisities,
      visible: !!selectedSession.value?.corequisities,
    },
    {
      title: "HOW TO REGISTER",
      description: "You will indicate which courses you want to take during the API onboarding process.",
      visible: true,
    },
    {
      title: "Financial Aid",
      description:
        "If you are paying for all or a portion of your experience with financial aid, keep in mind that there are requirements to enroll in, and maintain, a certain number of credits in order for the aid to be disbursed.  Check with your financial aid counselor to verify your enrollment requirements.  For undergraduates, full time is usually 12 credits and halftime is 6 credits.  Graduate requirements will vary.",
      visible: !!learnerTypesForInfoParagraph.includes(program.learnerType?.id),
    },
  ];
});

const images = computed(
  () =>
    entitySupplement.value?.supporting_media
      ?.filter((item) => CLOUDINARY_VIDEO_EXTENSION_TYPES.some((type) => item && !item.endsWith(type)))
      ?.map((img) =>
        imageTransformation({
          ...CLOUDINARY_DEFAULT_IMAGE_TRANSFORMATION,
          height: 358,
          width: 1160,
          path: img,
          compress: true,
        }),
      ) || [],
);

const creditType = computed(() => {
  const creditUnitName = hostInstitution.value?.credit_unit?.name;
  return creditUnitName && CONTACT_HOURS.includes(creditUnitName.trim()) ? "Contact Hours" : creditUnitName || "";
});

const accreditations = computed(() => {
  return hostInstitution.value?.accreditation?.join(", ") || "";
});

const languages = computed(() => {
  const hostLanguages = hostInstitutionLanguages.value ?? [];
  return hostLanguages.length > 0 ? `${hostLanguages?.map(({ value }) => value).join(", ")}` : "";
});

const showHostInstitutionFacts = computed(() => {
  const hasSessionHostName = Boolean(hostInstitution.value?.name);
  return (
    hasSessionHostName && (Boolean(accreditations.value) || Boolean(languages.value) || Boolean(creditType.value))
  );
});

onMounted(() => {
  setCredit();
});
</script>
<template>
  <div class="bg-white font-montserrat h-full">
    <div
      v-if="hasCredits"
      class="p-10 mt-13 bg-abstract-pattern"
      :class="[secondaryColor ? `bg-gray-275` : `bg-indigo-light bg-opacity-30`]"
    >
      <div class="text-indigo-base">
        <h1 data-testid="courses-header" class="font-bold text-2.5xl">
          {{ titleCopy }}
        </h1>
        <p data-testid="credit-description" class="font-medium">
          {{ descriptionCopy }}
        </p>
      </div>
    </div>
    <div data-testid="what-to-know-section" class="bg-white p-10 text-indigo-base">
      <!-- What You Should Know section -->
      <h1 class="font-bold text-2.5xl">What You Should Know</h1>
      <div v-for="(bullet, index) in infoParagraphs" :key="index">
        <h2 v-if="bullet.visible" class="font-bold text-base uppercase leading-normal mt-6">
          {{ bullet.title }}
        </h2>
        <p v-if="bullet.visible" class="text-sm font-medium leading-snug text-left mt-2">
          {{ bullet.description }}
        </p>
      </div>
      <div v-if="hasAcademicDescription" data-testid="academic-section">
        <h2 class="font-bold text-base uppercase leading-normal mt-6">
          ACADEMIC INFORMATION
        </h2>
        <JSONtoHTML
          ref="paragraphRef"
          data-testid="academic-description"
          :tiptap-json="academicDescription"
          :custom-class="['text-sm font-medium leading-snug text-left mt-2', isClamped ? 'line-clamp-5' : '']"
        >
          <div v-if="isToggleVisible" class="flex justify-start mt-2">
            <button
              data-testid="toggle-expansion"
              type="button"
              :class="`font-bold text-base text-left mt-2 ${secondaryColorClass}`"
              :aria-expanded="isExpanded"
              @click="toggleExpansion"
            >
              {{ isExpanded ? "- less" : "+ more" }}
            </button>
          </div>
        </JSONtoHTML>
      </div>
      <div v-if="showHostInstitutionFacts" data-testid="host-institution-facts">
        <h1 class="mt-20 font-bold text-indigo-base text-2.5xl mb-6">
          Facts About
          {{ selectedSession.value?.session_hosting_institutions[0]?.entity?.name }}
        </h1>
        <div v-if="images.length > 0" class="w-full h-fit pb-10 mx-auto md:pr-[104px]">
          <img
            :src="images?.[0]"
            loading="lazy"
            alt=""
            class="w-full max-w[405px] max-h-[125px] md:max-w-[1160px] md:max-h-[358px]"
          />
        </div>
        <h2
          v-if="creditType"
          class="font-montserrat text-indigo-base text-lg font-bold not-italic leading-6 capitalize mb-4"
        >
          Credit Type :
          <span class="leading-8 font-medium" data-testid="credit-type">{{ creditType }}</span>
        </h2>
        <h2
          v-if="accreditations"
          class="font-montserrat text-indigo-base text-lg font-bold not-italic leading-6 capitalize mb-4"
        >
          Accreditation :
          <span class="leading-8 font-medium">{{ accreditations }}</span>
        </h2>
      </div>
    </div>
    <StickyFooter
      max-next-width="max-w-[260px]"
      :disabled-next="v$.$invalid"
      :show-required-section="false"
      @back="goToPreviousStep"
      @next="goToNextStep"
    />
  </div>
</template>
